import { brokerOrgJsonLd } from "@/lib/seo-schema";
import { PageDatesBadge } from "@/components/site/PageDatesBadge";
import { CountyInsightsPanel } from "@/components/site/CountyInsightsPanel";
import { LocalMarketNews } from "@/components/site/LocalMarketNews";
import { getCountyInsights } from "@/lib/county-insights.functions";
import { RenterDemandPanel } from "@/components/site/RenterDemandPanel";
import { getState as getStateForCounty } from "@/data/states";
import { absoluteUrl } from "@/lib/site-origin";
import { fitTitle, fitDescription } from "@/lib/seo-head";
import { ExpertInsight } from "@/components/site/SeoBlocks";
import { buildCityExpertInsight } from "@/data/expert-insights";
import { states as ALL_STATES } from "@/data/states";
import { ProductReportCtaBanner } from "@/components/site/ProductReportCtaBanner";
import { SisterProductLinks } from "@/components/site/SisterProductLinks";
import { createFileRoute, Link, notFound, useNavigate } from "@tanstack/react-router";
import { zodValidator, fallback } from "@tanstack/zod-adapter";
import { z } from "zod";
import { SiteShell, PageHero } from "@/components/site/SiteShell";
import { QuickAnswer, PageOutro } from "@/components/site/PageEssentials";
import { Section, SectionHeader, FAQ, Breadcrumbs, CTABand, StatRow, TableOfContents, LoanGuidelines } from "@/components/site/Blocks";
import { RentalIncomeCompare } from "@/components/site/RentalIncomeCompare";
import { InvestmentRationale } from "@/components/site/InvestmentRationale";
import { DataSources } from "@/components/site/DataSources";
import { MarketUpdateSection } from "@/components/site/MarketUpdateSection";
import { CityGuideDownload } from "@/components/site/CityGuideDownload";
import { LandlordLawPanel } from "@/components/site/LandlordLawPanel";
import { DscrToolkit } from "@/components/site/DscrToolkit";
import { LiveDscrRateSection } from "@/components/rates/LiveDscrRateSection";
import { cityRateSectionCopy } from "@/lib/dscr-rate-copy";
import { RentalStrategies } from "@/components/site/RentalStrategies";
import { getProductHero } from "@/data/productHeroes";
import { getCityHero } from "@/data/city-heroes";
import { getState } from "@/data/states";

import { findCity, publishedInvestorCities, type InvestorCity } from "@/data/investor-cities";
import { requiresEntity } from "@/data/licensed-states";
import { getMarketStats, type MarketStats } from "@/lib/market-stats.functions";
import {
  localBusinessJsonLd,
  cityArticleJsonLd,
  faqJsonLd,
  breadcrumbListJsonLd,
  serviceJsonLd,
} from "@/lib/seo-schema";
import { cityKeywordsMeta } from "@/lib/page-keywords";
import { makeReportInlineCTA } from "@/components/site/ReportCta";
import { stateScenarioLocation } from "@/lib/state-location";

const InlineCTA = makeReportInlineCTA("dscr");

const searchSchema = z.object({
  // Optional (no default) so the clean canonical URL is served with 200 instead
  // of redirecting to ?income=section8; the component treats missing as section8.
  income: fallback(z.string().optional(), undefined).optional(),
});

function fmtCurrency(n: number | null | undefined): string | null {
  if (n == null) return null;
  return `$${Intl.NumberFormat("en-US").format(Math.round(n))}`;
}

function fmtPopulation(n: number | null | undefined): string | null {
  if (n == null) return null;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${Math.round(n / 1_000)}K`;
  return Intl.NumberFormat("en-US").format(n);
}

/** Overlay live Census stats on the curated city record where available. */
function withLiveStats(city: InvestorCity, stats: MarketStats | null): InvestorCity & { liveVintage?: string | null } {
  if (!stats) return city;
  return {
    ...city,
    medianHomeValue: fmtCurrency(stats.medianHomeValue) ?? city.medianHomeValue,
    population: fmtPopulation(stats.population) ?? city.population,
    liveVintage: stats.vintage,
  };
}

export const Route = createFileRoute("/dscr-loans/$state/$city")({
  validateSearch: zodValidator(searchSchema),
  loader: async ({ params }) => {
    const rawCity = findCity(params.state, params.city);
    if (!rawCity) throw notFound();
    const countyInsights = await getCountyInsights({
      data: {
        stateAbbrev: getStateForCounty(params.state)?.abbrev ?? "",
        countyName: rawCity.county,
        placeLabel: `${rawCity.name}, ${rawCity.stateName}`,
      },
    }).catch(() => null);
    const stats = await getMarketStats({ data: { stateSlug: params.state, citySlug: params.city } }).catch(() => null);
    const city = withLiveStats(rawCity, stats);
    return {
      countyInsights,
      marketStats: stats,
      city,
      liveVintage: stats?.vintage ?? null,
      liveMedianRent: stats?.medianRent ?? null,
      liveMedianRent2BR: stats?.medianRent2BR ?? null,
      liveMedianRent3BR: stats?.medianRent3BR ?? null,
      liveUnemploymentRate: stats?.unemploymentRate ?? null,
      liveUnemploymentPeriod: stats?.unemploymentPeriod ?? null,
    };
  },

  head: ({ loaderData, params }) => {
    if (!loaderData) {
      return { meta: [{ title: "City not found" }, { name: "robots", content: "noindex" }] };
    }
    const c = loaderData.city;
    const title = fitTitle(`DSCR Loans in ${c.name}, ${c.stateName} | Local Rental Financing`);
    const desc = `DSCR loans in ${c.name}, ${c.stateName}: local HUD FMR, rental strategies, cap rates, landlord rules, DSCR tools, and same-day scenario review on complete applications. NMLS #2620881.`.slice(0, 158);
    const url = `/dscr-loans/${params.state}/${params.city}`;
    const faqs = cityFaqs(c.name, c.stateName);
    const hero = getCityHero(params.state, params.city, c.name, c.stateName);
    return {
      meta: [
        { title },
        { name: "description", content: desc },
        { name: "keywords", content: cityKeywordsMeta("dscr-loans", c.name, c.stateName) },
        { name: "robots", content: "index, follow, max-image-preview:large" },
        { property: "og:title", content: title },
        { property: "og:description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://dscrloanmarket.com${url}` },
        ...(hero ? [{ property: "og:image", content: absoluteUrl(hero.src) }, { property: "og:image:alt", content: hero.alt }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: desc },
        ...(hero ? [{ name: "twitter:image", content: absoluteUrl(hero.src) }] : []),
        { property: "article:published_time", content: "2026-01-01" },
      ],
      links: [
        { rel: "canonical", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "en-us", href: `https://dscrloanmarket.com${url}` },
        { rel: "alternate", hrefLang: "x-default", href: `https://dscrloanmarket.com${url}` },
        ...(hero ? [{ rel: "preload", as: "image", href: hero.src }] : []),
      ],
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(faqJsonLd(faqs)) },
        { type: "application/ld+json", children: JSON.stringify(serviceJsonLd({ url, serviceName: `DSCR Loans in ${c.name}, ${c.stateName}`, description: desc, state: c.stateName, city: c.name })) },
        { type: "application/ld+json", children: JSON.stringify(cityWebPageJsonLd({ url, city: c.name, state: c.stateName, description: desc })) },
        { type: "application/ld+json", children: JSON.stringify(cityLoanProductJsonLd({ url, city: c.name, state: c.stateName, description: desc })) },
        { type: "application/ld+json", children: JSON.stringify(cityHowToJsonLd({ url, city: c.name, state: c.stateName })) },
        { type: "application/ld+json", children: JSON.stringify(cityDatasetJsonLd({ url, city: c.name, state: c.stateName, zip: c.centroidZip })) },
        { type: "application/ld+json", children: JSON.stringify(citySpeakableJsonLd({ url })) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            localBusinessJsonLd({ url, state: c.stateName, city: c.name }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            cityArticleJsonLd({
              url,
              headline: `DSCR Loans in ${c.name}, ${c.stateName}`,
              description: desc,
              image: hero?.src,
            }),
          ),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            breadcrumbListJsonLd([
              { name: "Home", url: "/" },
              { name: "DSCR Loans", url: "/dscr-loans" },
              { name: c.stateName, url: `/dscr-loans/${params.state}` },
              { name: c.name, url },
            ]),
          ),
        },
      ],
    };
  },
  component: CityPage,
  notFoundComponent: () => (
    <SiteShell hideBreadcrumbs>
      <Section>
        <div className="mx-auto max-w-2xl py-16 text-center">
          <h1 className="font-display text-3xl font-bold">City not found</h1>
          <p className="mt-2 text-muted-foreground">Try our full <Link to="/where-we-lend" className="text-brand underline">list of states where we lend</Link>.</p>
        </div>
      </Section>
    </SiteShell>
  ),
});

function cityFaqs(city: string, state: string) {
  return [
    { q: `Do you lend on DSCR investment properties in ${city}, ${state}?`, a: `Yes. We fund business-purpose DSCR rental loans in ${city} and throughout ${state} on 1–4 unit residential, warrantable condos, townhomes, and select small multi-family properties.` },
    { q: `What is the minimum DSCR to qualify in ${city}?`, a: `Most programs allow a minimum DSCR of 1.00, with best pricing at 1.20 or higher. In ${city}, current rents and taxes typically support 1.10–1.30 DSCR on well-priced deals.` },
    { q: `What is the maximum LTV on a ${city} DSCR loan?`, a: `Up to 80% LTV on purchases — 85% only as an exception at 740+ FICO on an SFR with a 30-year fixed — and 70–75% on cash-out refinances, subject to credit score, DSCR, reserves, and property type. Best pricing is usually 65–70% LTV.` },
    { q: `Can I close in an LLC or entity in ${city}?`, a: `Yes. LLC, LP, S-Corp, C-Corp, and trust vesting are all supported with a member/manager personal guarantee. This is standard for business-purpose loans in ${state}.` },
    { q: `Do you lend to foreign nationals buying in ${city}?`, a: `Yes. Foreign nationals and ITIN borrowers can purchase or refinance ${city} rentals with a DSCR loan — no US credit or US tax returns required, up to 75% LTV on purchase and 70% on cash-out.` },
    { q: `What credit score do I need for a ${city} DSCR loan?`, a: `Programs start at a 660 FICO, with the strongest pricing at 720+. Lower scores may still qualify with stronger DSCR, larger down payment, or additional reserves.` },
    { q: `How is rental income calculated on a ${city} property?`, a: `We use the lower of the executed lease or the appraiser's Form 1007 market rent. Short-term rental income can be used with 12 months of history or AirDNA/permit-supported projections where local rules allow.` },
    { q: `Can I use Section 8 / HUD FMR rents in ${city}?`, a: `Yes. If the unit is enrolled in the Housing Choice Voucher program, the HAP contract rent counts as qualifying income. HUD FMR is also a useful benchmark for underwriting ${city} deals to realistic rent.` },
    { q: `Are short-term rentals (Airbnb/VRBO) allowed for DSCR in ${city}?`, a: `Yes, where the city and HOA permit it. ${city} STR deals need proof the property is legally permitted, plus supporting income data (existing bookings or market data) to underwrite.` },
    { q: `How much money do I need down to buy in ${city}?`, a: `Plan on 20–25% down plus closing costs and 6 months of PITIA reserves. Exact amount depends on DSCR, credit, and loan size.` },
    { q: `How fast can we close a DSCR loan in ${city}?`, a: `Typical close timeline is 15–25 business days after title is opened and appraisal is ordered. Clean files with entity docs ready can close faster.` },
    { q: `Are there prepayment penalties on ${city} DSCR loans?`, a: `Most DSCR programs carry a step-down prepay (commonly 5/4/3/2/1 or 3-year) with buy-down options. We'll price with and without prepay so you can compare.` },
    { q: `Can I cash-out refinance a rental I already own in ${city}?`, a: `Yes. Cash-out refinances up to 75% LTV are available on seasoned ${city} rentals — commonly used to recycle capital into the next deal (BRRRR).` },
    { q: `Do you finance BRRRR or fix-and-flip projects in ${city}?`, a: `Yes. We offer bridge and fix-and-flip loans for the acquisition/rehab phase in ${city}, then convert to a long-term DSCR loan once the property is stabilized and leased.` },
    { q: `How do property taxes and insurance affect DSCR in ${city}?`, a: `We underwrite to post-closing tax reality (not the seller's current bill) plus real insurance quotes — including wind, flood, and HOA where applicable. This is the #1 reason ${city} DSCRs come in lower than expected.` },
  ];
}


function cityWebPageJsonLd(opts: { url: string; city: string; state: string; description: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": `${opts.url}#webpage`,
    url: opts.url,
    name: `DSCR Loans in ${opts.city}, ${opts.state}`,
    description: opts.description,
    inLanguage: "en-US",
    isPartOf: { "@type": "WebSite", name: "Simply Approved Mortgages", url: "/" },
    about: { "@type": "LoanOrCredit", name: `DSCR rental property loan in ${opts.city}, ${opts.state}` },
    mainEntity: { "@id": `${opts.url}#loan-product` },
  };
}

function cityLoanProductJsonLd(opts: { url: string; city: string; state: string; description: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "FinancialProduct",
    "@id": `${opts.url}#loan-product`,
    name: `DSCR Loans in ${opts.city}, ${opts.state}`,
    url: opts.url,
    description: opts.description,
    category: "Business-purpose rental property mortgage",
    provider: brokerOrgJsonLd(),
    areaServed: [
      { "@type": "City", name: opts.city, containedInPlace: { "@type": "State", name: opts.state } },
      { "@type": "State", name: opts.state },
    ],
    feesAndCommissionsSpecification: "Loan amounts generally $100,000–$3,500,000; up to 80% LTV (85% by exception at 740+ FICO on an SFR); DSCR floor 0.75 and otherwise varies by program, credit tier, reserves, and property type.",
  };
}

function cityHowToJsonLd(opts: { url: string; city: string; state: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: `How to get DSCR loan pricing in ${opts.city}, ${opts.state}`,
    description: `Steps to price a business-purpose DSCR rental property loan in ${opts.city}, ${opts.state}.`,
    url: opts.url,
    step: [
      { "@type": "HowToStep", name: "Send the property scenario", text: "Provide the subject address, purchase price or payoff, estimated rent, taxes, insurance, HOA, and rental strategy." },
      { "@type": "HowToStep", name: "Verify rental income", text: "Compare long-term lease or market rent, HUD FMR, and short-term rental assumptions where applicable." },
      { "@type": "HowToStep", name: "Run DSCR and LTV", text: "Calculate DSCR using rent divided by PITIA and size the loan to program LTV and reserve limits." },
      { "@type": "HowToStep", name: "Receive initial scenario review", text: "A Simply Approved Mortgages loan officer reviews the file and responds with scenario-specific pricing feedback." },
    ],
  };
}

function cityDatasetJsonLd(opts: { url: string; city: string; state: string; zip: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: `HUD FMR and rental strategy data for ${opts.city}, ${opts.state}`,
    description: `ZIP-level HUD Fair Market Rent lookup and rental strategy context used on the ${opts.city}, ${opts.state} DSCR guide.`,
    url: `${opts.url}#income`,
    spatialCoverage: {
      "@type": "Place",
      name: `${opts.city}, ${opts.state}`,
      address: { "@type": "PostalAddress", postalCode: opts.zip, addressCountry: "US" },
    },
    temporalCoverage: "2026",
    creator: { "@type": "Organization", name: "Simply Approved Mortgages" },
    isBasedOn: "https://www.huduser.gov/portal/dataset/fmr-api.html",
  };
}

function citySpeakableJsonLd(opts: { url: string }) {
  return {
    "@context": "https://schema.org",
    "@type": "SpeakableSpecification",
    cssSelector: ["h1", "#tldr", "#income", "#guidelines"],
    url: opts.url,
  };
}

function CityPage() {
  const { city, liveVintage, liveMedianRent, liveMedianRent2BR, liveMedianRent3BR, liveUnemploymentRate, liveUnemploymentPeriod, countyInsights, marketStats } = Route.useLoaderData();
  const entity = requiresEntity(city.stateSlug);
  const state = getState(city.stateSlug);
  const stateCities = publishedInvestorCities.filter((c) => c.stateSlug === city.stateSlug && c.slug !== city.slug);
  const otherCities = (stateCities.length > 0 ? stateCities : publishedInvestorCities.filter((c) => c.slug !== city.slug)).slice(0, 8);
  const hero = getCityHero(city.stateSlug, city.slug, city.name, city.stateName) ?? getProductHero("dscr-loans");
  const topMarkets = state?.topMarkets ?? [city.name];
  const localEconomy = city.economy ?? state?.marketNote ?? `${city.name} sits inside a service-area investor market where DSCR files should be underwritten to verified rent, taxes, insurance, HOA dues, and local rental rules.`;
  const localTransit = city.transit ?? `Review neighborhood access to employment centers, schools, medical services, transit routes, and major roads before assuming long-term tenant demand in ${city.name}.`;
  const localLivability = city.livability ?? `${city.name} deals should be checked at the neighborhood level for vacancy, insurance, taxes, HOA restrictions, short-term-rental rules, and landlord/tenant timelines.`;
  const localDemandDrivers = city.demandDrivers?.length ? city.demandDrivers : city.strengths;
  return (
    <SiteShell hideBreadcrumbs productLabel="DSCR">
      <PageHero
        eyebrow={`${city.name}, ${city.stateName}`}
        title={`DSCR Loans in ${city.name}, ${city.stateName}`}
        subtitle={`Business-purpose rental financing for investors buying, refinancing, or scaling in ${city.name}. Local rent + tax context built in.`}
        image={hero}
      />
      <Section>
        <Breadcrumbs items={[
          { name: "Home", href: "/" },
          { name: "DSCR Loans", href: "/dscr-loans" },
          { name: city.stateName, href: `/dscr-loans/${city.stateSlug}` },
          { name: city.name },
        ]} />
      </Section>

      <QuickAnswer
        question={`Can I get an investment property loan in ${city.name}, ${city.stateName}?`}
        answer={`Yes — Simply Approved Mortgages arranges business-purpose financing on non-owner-occupied investment property in ${city.name}, ${city.stateName}, qualified on the property's rental cash flow rather than personal tax returns. Every figure on this page is an estimate and remains subject to a complete application, credit and property review, appraisal where required, and full underwriting.`}
      />
      <TableOfContents
        items={[
          { href: "#tldr", label: "TL;DR" },
          { href: "#snapshot", label: "Market snapshot" },
          { href: "#income", label: "Rental income" },
          { href: "#rental-strategies", label: "Rental strategies" },
          { href: "#fundamentals", label: "Investment fundamentals" },
          { href: "#guidelines", label: "Loan guidelines" },
          { href: "#landlord", label: "Landlord law" },
          { href: "#tools", label: "Fit tools" },
          { href: "#local-links", label: `${city.stateName} links` },
          { href: "#faq", label: "FAQ" },
        ]}
      />

      <Section id="tldr">
        <div className="rounded-2xl border border-brand-gold/30 bg-brand-gold/5 p-6 md:p-8">
          <div className="mb-3 text-xs font-semibold uppercase tracking-widest text-brand">
            TL;DR — DSCR in {city.name}
          </div>
          <ul className="grid gap-2 text-sm md:grid-cols-2">
            <li>• Median home value: <strong>{city.medianHomeValue}</strong></li>
            <li>• Median 2BR rent: <strong>{city.medianRent2BR}</strong></li>
            <li>• Cap-rate band: <strong>{city.capRateBand}</strong></li>
            <li>• Population: <strong>{city.population}</strong></li>
            <li>• Centroid ZIP for HUD FMR: <strong>{city.centroidZip}</strong></li>
            <li>• Best rental strategies: <strong>SFR, Section 8, Airbnb / STR where permitted</strong></li>
            <li>• Min DSCR: <strong>1.00 (best pricing at 1.20+)</strong></li>
            <li>• Max LTV: <strong>up to 80% purchase</strong></li>
            <li>• Vesting: <strong>LLC, LP, S/C-Corp</strong>{entity ? " (required in this state)" : ""}</li>
            <li>• Typical close: <strong>15–21 business days</strong></li>
          </ul>
        </div>
      </Section>

      <Section id="snapshot">
        <div className="grid gap-8 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-8">
            <StatRow items={[
              { label: "Population", value: city.population },
              { label: "Median home value", value: city.medianHomeValue },
              { label: liveMedianRent2BR ? "Median 2BR rent" : (liveMedianRent ? "Median gross rent" : "Median 2BR rent"), value: liveMedianRent2BR ? `$${liveMedianRent2BR.toLocaleString()}/mo` : (liveMedianRent ? `$${liveMedianRent.toLocaleString()}/mo` : city.medianRent2BR) },
              { label: liveMedianRent3BR ? "Median 3BR rent" : "Cap rate band", value: liveMedianRent3BR ? `$${liveMedianRent3BR.toLocaleString()}/mo` : city.capRateBand },
            ]} />
            {(liveVintage || liveUnemploymentRate != null) && (
              <p className="mt-2 text-xs text-muted-foreground">
                {liveVintage && (<>Population, median home value, and median rent by bedroom live from U.S. Census ACS {liveVintage} (B25031). </>)}
                {liveUnemploymentRate != null && (<>Statewide unemployment: <strong>{liveUnemploymentRate.toFixed(1)}%</strong>{liveUnemploymentPeriod ? ` (${liveUnemploymentPeriod})` : ""} — U.S. Bureau of Labor Statistics LAUS, updated monthly.</>)}
              </p>
            )}
            <div className="prose max-w-none">
              <h2 className="font-display text-2xl font-bold text-brand-dark">Market snapshot</h2>
              <p className="mt-3 text-muted-foreground">{city.narrative}</p>
              {city.county && <p className="mt-2 text-sm text-muted-foreground">County: {city.county} · Centroid ZIP: {city.centroidZip}</p>}
              <p className="mt-3 text-muted-foreground">
                Comparing options? See our <Link to="/dscr-loans" className="font-semibold text-brand underline underline-offset-2">DSCR loan program</Link> or the <Link to="/dscr-loans/$state" params={{ state: city.stateSlug }} className="font-semibold text-brand underline underline-offset-2">{city.stateName} DSCR guide</Link> for state-level rules and other markets we lend in.
              </p>

            </div>
            <SisterProductLinks
              self="dscr"
              stateSlug={city.stateSlug}
              stateName={city.stateName}
              citySlug={city.slug}
              cityName={city.name}
            />
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Strengths</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {city.strengths.map((s: string) => <li key={s}>• {s}</li>)}
                </ul>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">Watch-outs</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  {city.watchouts.map((s: string) => <li key={s}>• {s}</li>)}
                </ul>
              </div>
            </div>
            {entity && (
              <div className="rounded-xl border border-brand-gold/40 bg-brand-gold/5 p-5 text-sm">
                <strong className="text-brand-dark">Entity vesting required in {city.stateName}.</strong>{" "}
                Business-purpose loans in this state must vest in an LLC, LP, or similar entity. We can guide setup at pre-approval.
              </div>
            )}
            <div className="grid gap-6 md:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">What to have ready</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• Subject property address and purchase price or payoff</li>
                  <li>• Executed lease, appraiser 1007, or STR revenue history</li>
                  <li>• LLC / entity docs (or we'll help you form one)</li>
                  <li>• Two months of reserves in a verifiable account</li>
                  <li>• Property insurance quote for the subject address</li>
                </ul>
              </div>
              <div className="rounded-xl border border-border bg-card p-5">
                <h3 className="font-semibold text-brand-dark">How {city.name} typically prices</h3>
                <ul className="mt-2 space-y-1 text-sm text-muted-foreground">
                  <li>• Best pricing at DSCR 1.20+ with 25% down</li>
                  <li>• Foreign national: up to 75% LTV (80% select cases)</li>
                  <li>• STR income accepted with 12-mo history or AirDNA comps</li>
                  <li>• Section 8 HAP contract rent qualifies at face value</li>
                  <li>• Close in 15–21 business days once docs are in</li>
                </ul>
              </div>
            </div>
          </div>
          <div className="space-y-6">
            <CityGuideDownload city={city} />
            <div id="lead-form" className="rounded-xl border border-border bg-card p-6 shadow-sm">
              <h3 className="font-display text-lg font-bold text-foreground">Get pricing for {city.name}</h3>
              <p className="mt-2 text-sm text-muted-foreground">A licensed loan officer follows up with an initial scenario review once we have your credit authorization and supporting documents — usually the same business day. Formal pricing and terms follow underwriting.</p>
              <Link to="/apply" className="mt-4 inline-flex items-center justify-center rounded-md bg-brand-gold px-5 py-2.5 text-sm font-semibold text-primary hover:bg-brand-gold-hover">Start My Approval</Link>
              <Link to="/rates" className="mt-2 inline-flex items-center justify-center rounded-md border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground hover:border-brand hover:text-brand">See Today's Rates</Link>
            </div>

            <div className="rounded-xl border border-border bg-card p-5 text-xs text-muted-foreground">
              <div className="font-semibold text-foreground">Reviewed by Simply Approved Mortgages</div>
              <div className="mt-1">NMLS #2620881 · Business-purpose investor lending</div>
              <PageDatesBadge only="reviewed" className="mt-2" />
            </div>

            <div className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-semibold text-brand-dark">Other {stateCities.length > 0 ? city.stateName : "investor"} cities</h3>
              <ul className="mt-3 grid grid-cols-2 gap-2 text-sm">
                {otherCities.map((c) => (
                  <li key={`${c.stateSlug}-${c.slug}`}>
                    <Link to="/dscr-loans/$state/$city" params={{ state: c.stateSlug, city: c.slug }} className="text-brand hover:underline">
                      {c.name}, {c.stateName}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      <LiveDscrRateSection
            strategyCards
        {...cityRateSectionCopy(city)}
        scenarioOverrides={{ ...(state ? stateScenarioLocation(state.abbrev) : {}) }}
        toolLabel={`${city.name} DSCR`}
      />

      <Section id="income">
        <RentalIncomeCompareControlled
          zip={city.centroidZip}
          locationLabel={`${city.name}, ${city.stateName}`}
          medianRent2BR={city.medianRent2BR}
          medianHomeValue={city.medianHomeValue}
          airbnbAdr={city.airbnbAdr}
          airbnbOccupancy={city.airbnbOccupancy}
        />
      </Section>

      <RentalStrategies />

      <InlineCTA
        title={`Compare rental income on a ${city.name} address`}
        body={`Use Section 8 FMR, single-family market rent, and short-term-rental assumptions before you make an offer — then send us the scenario for written DSCR pricing.`}
        cta="Get Your Personalized Rate Quote"
        variant="soft"
      />

      <Section id="fundamentals">
        <InvestmentRationale
          location={`${city.name}, ${city.stateName}`}
          economy={localEconomy}
          employers={city.employers}
          transit={localTransit}
          livability={localLivability}
          demandDrivers={localDemandDrivers}
        />
        <div className="mt-6">
          <DataSources locationLabel={`${city.name}, ${city.stateName}`} />
        </div>
      </Section>

      <Section>
        <SectionHeader
          eyebrow="Local underwriting checklist"
          title={`What can make or break a ${city.name} DSCR approval`}
          intro="The loan does not qualify from rent alone. These are the local file items we want investors to check before locking in a purchase contract."
        />
        <div className="mt-8 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
          {[
            { title: "Real rent support", body: `Use the executed lease, appraiser rent schedule, HUD FMR for ZIP ${city.centroidZip}, or permitted STR data — not wishful rent.` },
            { title: "Taxes after transfer", body: `${city.name} files should be underwritten to post-closing tax reality, not only the seller's current assessed bill.` },
            { title: "Insurance & HOA", body: "Wind, flood, condo master policy, HOA dues, and special assessments all hit PITIA and can push DSCR below target." },
            { title: "Entity & occupancy", body: `${entity ? `${city.stateName} requires business-purpose entity vesting for this lending path.` : "LLC, LP, trust, S-Corp, or C-Corp vesting is supported."} The property must be non-owner-occupied.` },
          ].map((item) => (
            <article key={item.title} className="rounded-xl border border-border bg-card p-5">
              <h3 className="font-display text-base font-semibold text-foreground">{item.title}</h3>
              <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
            </article>
          ))}
        </div>
      </Section>



      <InlineCTA
        title={`Price a ${city.name} DSCR deal`}
        body="Submit a complete application with address, price or payoff, rent, credit authorization, and supporting documents — a licensed LO replies with an initial scenario review, usually same day. Formal pricing follows underwriting."
        cta="Get Your Personalized Rate Quote"
      />

      <Section id="guidelines">
        <LoanGuidelines
          productLabel={`DSCR · ${city.name}, ${city.stateName}`}
          rows={[
            { label: "Loan amount", value: "$100k – $3M+", why: "Sized to appraised value, program caps, and market comps." },
            { label: "Max LTV", value: "Up to 80% purchase / 75% cash-out", why: "Best pricing typically ~5% below the LTV cap." },
            { label: "Min DSCR", value: "1.00 (best at 1.20+)", why: `In ${city.name}, current 2BR rent near ${city.medianRent2BR} typically supports 1.10–1.30 DSCR.` },
            { label: "Reserves", value: "6 months PITIA", why: "Verified in a bank statement after close." },
            { label: "Terms", value: "30-yr fixed, 5/6 & 7/6 ARM, I/O options", why: "Rate/term picked to match hold period." },
            { label: "Vesting", value: entity ? "LLC / LP / S-Corp / C-Corp (entity required)" : "Individual or LLC / LP / S-Corp / C-Corp", why: "Business-purpose loans close in your entity of choice." },
          ]}
          note="Ranges are typical program guidelines — not a commitment to lend. Final terms are established in an initial scenario review (formal pricing after underwriting) after underwriting review."
        />
      </Section>

      <Section id="landlord">
        <LandlordLawPanel stateSlug={city.stateSlug} stateName={city.stateName} />
      </Section>

      <MarketUpdateSection productSlug="dscr-loans" productLabel={`DSCR · ${city.name}, ${city.stateName}`} />

      <Section>
        <ProductReportCtaBanner productKey="dscr" variant="full" locationLabel={`${city.name}, ${city.stateName}`} />
      </Section>


      <DscrToolkit
        context={`DSCR investor loan — ${city.name}, ${city.stateName}`}
        title={`${city.name} DSCR fit tools`}
        intro={`Estimate DSCR on a ${city.name} rental, find the largest loan the property qualifies for, and generate a scenario a licensed LO can price today.`}
      />

      <Section id="local-links">
        <SectionHeader
          eyebrow="Keep exploring"
          title={`More ${city.stateName} investor resources`}
          intro={`Jump into the ${city.stateName} state guide, our DSCR program, current rates, and other ${city.stateName} markets we lend in.`}
        />

        <div className="mt-8 grid gap-5 lg:grid-cols-3">
          <Link to="/dscr-loans" className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Program hub</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">DSCR loan requirements →</h3>
            <p className="mt-2 text-sm text-muted-foreground">Rates, LTV, DSCR formula, documents, business-purpose rules, and the full application path.</p>
          </Link>
          <Link to="/dscr-loans/$state" params={{ state: city.stateSlug }} className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">State guide</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">{city.stateName} DSCR guide →</h3>
            <p className="mt-2 text-sm text-muted-foreground">State-level lending notes, top markets, landlord rules, rental benchmarks, and links to city pages.</p>
          </Link>
          <Link to="/rates" className="group rounded-xl border border-border bg-card p-5 transition hover:border-brand">
            <div className="text-xs font-semibold uppercase tracking-wide text-brand-gold">Pricing</div>
            <h3 className="mt-2 font-display text-lg font-bold text-foreground group-hover:text-brand">See Today's Rates →</h3>
            <p className="mt-2 text-sm text-muted-foreground">Review current rate context before sending the subject property for exact an initial scenario review (formal pricing after underwriting).</p>
          </Link>
        </div>
        <div className="mt-8">
          <h3 className="font-display text-xl font-bold text-foreground">Top {city.stateName} markets investors compare with {city.name}</h3>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {otherCities.map((c) => (
              <Link
                key={`${c.stateSlug}-${c.slug}`}
                to="/dscr-loans/$state/$city"
                params={{ state: c.stateSlug, city: c.slug }}
                className="rounded-lg border border-border bg-card px-4 py-3 text-sm transition hover:border-brand hover:text-brand"
              >
                <span className="font-semibold">{c.name}</span>
                <span className="block text-xs text-muted-foreground">{c.medianRent2BR} 2BR rent · {c.capRateBand} cap</span>
              </Link>
            ))}
          </div>
          {state && (
            <p className="mt-4 text-sm text-muted-foreground">
              Other {city.stateName} markets we lend in include {topMarkets.join(", ")}.
            </p>
          )}

        </div>
      </Section>


      <InlineCTA
        title={`Price your ${city.name} DSCR deal`}
        body="Complete the short pre-qualification form and a licensed loan officer replies with an initial scenario review."
        cta="Get Your Personalized Rate Quote"
      />

      <Section id="faq">
        <SectionHeader
          eyebrow="FAQ"
          title={`${city.name} DSCR loan questions`}
          intro={`Answers to the most common investor questions about DSCR loans in ${city.name}, ${city.stateName}.`}
          center
        />
        <div className="mt-8">
          <FAQ items={cityFaqs(city.name, city.stateName)} />
        </div>
      </Section>

      <ExpertInsight insight={buildCityExpertInsight(city.name, city.stateName, "dscr-loans", "DSCR loan", ALL_STATES.find((s) => s.name === city.stateName))} />

      <CTABand title={`Ready for real pricing on your ${city.name} DSCR deal?`} body={`Complete the short pre-qualification form and upload your supporting documents, and a licensed loan officer will follow up with an initial scenario review for ${city.name}, ${city.stateName}.`} />
      <LocalMarketNews
        stateName={city.stateName}
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        cityName={city.name}
        countyName={city.county ?? null}
        stats={marketStats ?? null}
        statsScope="city"
        countyInsights={countyInsights ?? null}
      />
      <CountyInsightsPanel
        stateAbbrev={getStateForCounty(city.stateSlug)?.abbrev ?? ""}
        stateName={city.stateName}
        countyName={city.county}
        placeLabel={`${city.name}, ${city.stateName}`}
        initialData={countyInsights}
      />
      <RenterDemandPanel countyName={city.county ?? null} stateName={city.stateName} />


      <PageOutro hideInsight />
    </SiteShell>
  );
}

function RentalIncomeCompareControlled(
  props: Omit<React.ComponentProps<typeof RentalIncomeCompare>, "value" | "onChange">,
) {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const mode: "sfr" | "section8" | "airbnb" =
    search.income === "airbnb" ? "airbnb" : search.income === "sfr" ? "sfr" : "section8";
  return (
    <RentalIncomeCompare
      {...props}
      value={mode}
      onChange={(next) =>
        navigate({
          search: (prev: { income?: string }) => ({ ...prev, income: next }),
          replace: true,
          resetScroll: false,
        })
      }
    />
  );
}
